import { styled } from '@linaria/react';
import type { TFunction } from 'i18next';
import type { AgentMinerSession } from '../../../api/agent-miner-client/agent-miner-client.type';
import type { TableColumn } from '../../../components/table/table';
import { inkDark, inkDarkest, inkLight } from '../../../styles/design-tokens';
import { CategoryChip } from '../shared/category-chip';
import { fmtCost, fmtRelative } from '../shared/formatters';
import { TasksChipList } from '../shared/tasks-chip-list';
import { RelatedPrsCell } from './cells/related-prs-cell';
import { SourceCell } from './cells/source-cell';
import { TitleCell } from './cells/title-cell';
import { TokensCell } from './cells/tokens-cell';

/**
 * Build the agent-miner sessions-list table columns against the caller's
 * translation function so org-level text_config overrides apply.
 *
 * @param {TFunction} t - Translation function from `useTranslation()`
 * @param {boolean} hideApiCost - When true, omits the API cost column so orgs
 *   that opt out of cost visibility never render it.
 * @param {boolean} showRelatedPrs - When true, includes the Related PRs column;
 *   gated on the org having an active SCM integration.
 * @returns {TableColumn<AgentMinerSession>[]} Column definitions for `<Table>`
 */
const getSessionsListColumns = (
  t: TFunction,
  hideApiCost = false,
  showRelatedPrs = false,
): TableColumn<AgentMinerSession>[] => {
  const columns: TableColumn<AgentMinerSession>[] = [
    {
      key: 'source',
      label: t('agent_miner.source'),
      width: '140px',
      sortable: true,
      render: (_, row) => <SourceCell session={row} />,
    },
    {
      key: 'name',
      label: t('agent_miner.session'),
      width: '250px',
      sortable: true,
      render: (_, row) => <TitleCell session={row} />,
    },
    {
      key: 'flora_annotations',
      label: t('agent_miner.category'),
      width: '140px',
      sortable: false,
      render: (_, row) => <CategoryChip label={row.flora_annotations?.label ?? null} size="sm" />,
    },
    {
      key: 'user',
      label: t('agent_miner.user'),
      width: '120px',
      sortable: false,
      render: (_, row) => <UserName>{row.user?.name ?? '—'}</UserName>,
    },
    {
      key: 'linked_tasks',
      label: t('common.work_units'),
      width: '96px',
      sortable: false,
      render: (_, row) => <TasksChipList tasks={row.linked_tasks ?? []} size="sm" />,
    },
    {
      key: 'total_input_tokens',
      label: t('agent_miner.tokens'),
      width: '200px',
      sortable: false,
      render: (_, row) => <TokensCell session={row} />,
    },
    {
      key: 'last_turn_at',
      label: t('agent_miner.last_activity'),
      width: '110px',
      sortable: true,
      render: (_, row) => <StartedText>{fmtRelative(row.last_turn_at ?? row.started_at)}</StartedText>,
    },
  ];

  if (showRelatedPrs) {
    // Slot Related PRs right after Work units, matching the design's column order.
    const workUnitsIndex = columns.findIndex((column) => column.key === 'linked_tasks');
    columns.splice(workUnitsIndex + 1, 0, {
      key: 'related_prs',
      label: t('agent_miner.related_prs'),
      width: '120px',
      sortable: false,
      render: (_, row) => <RelatedPrsCell session={row} />,
    });
  }

  if (!hideApiCost) {
    // Insert the API cost column before the trailing "last activity" column to
    // preserve the original ordering when cost visibility is enabled.
    columns.splice(columns.length - 1, 0, {
      key: 'total_cost_usd',
      label: t('agent_miner.api_cost'),
      width: '80px',
      sortable: true,
      sortValue: (row) => Number(row.total_cost_usd ?? 0),
      render: (_, row) => <MonoRight>{fmtCost(row.total_cost_usd)}</MonoRight>,
    });
  }

  return columns;
};

export { getSessionsListColumns };

const MonoRight = styled.span`
  display: block;
  width: 100%;
  text-align: right;
  font-family: 'SF Mono', ui-monospace, Menlo, monospace;
  font-weight: 600;
  font-size: 12px;
  line-height: 1;
  color: ${inkDarkest};
  font-variant-numeric: tabular-nums;
`;

const StartedText = styled.span`
  display: block;
  width: 100%;
  text-align: right;
  font: 400 11.5px/1 'Figtree', sans-serif;
  color: ${inkLight};
`;

const UserName = styled.span`
  font: 500 12px/1.2 'Figtree', sans-serif;
  color: ${inkDark};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
